"use client"

import {
  createBuyNowCart,
  payWithWallet,
  quoteExpressShipping,
  selectExpressShipping,
} from "@lib/data/express-checkout"
import {
  loadMoneiJs,
  MoneiComponent,
  newMoneiSessionId,
} from "@lib/util/monei-js"
import ErrorMessage from "@modules/checkout/components/error-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useParams, useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

// Public MONEI account id (Dashboard > Settings > API). Without it the wallet
// button is hidden and only the checkout is offered.
const MONEI_ACCOUNT_ID = process.env.NEXT_PUBLIC_MONEI_ACCOUNT_ID

export type ExpressSource =
  | { type: "cart"; cartId: string }
  | { type: "product"; variantId: string; quantity: number }

type ExpressCheckoutProps = {
  source: ExpressSource | null
  /** Initial total in minor units shown in the wallet sheet */
  amount: number
  currency: string
  disabled?: boolean
  /**
   * Renders the "Comprar ya" trigger: it opens the one-page checkout of a
   * dedicated cart for the product, leaving the basket untouched.
   */
  renderBuyNow?: (
    open: () => void,
    disabled: boolean,
    redirecting: boolean
  ) => React.ReactNode
  /** Hide the terms line when the page already shows it */
  hideLegal?: boolean
}

const sourceKey = (s: ExpressSource | null) =>
  !s ? "" : s.type === "cart" ? s.cartId : `${s.variantId}:${s.quantity}`

/**
 * Fast checkout without registration, using MONEI:
 * - Apple Pay / Google Pay button: the wallet provides the shipping address,
 *   Packlink is quoted live and the order is placed without leaving the page.
 * - "Comprar ya": the one-page checkout (guest or logged in) of the product.
 */
export default function ExpressCheckout({
  source,
  amount,
  currency,
  disabled,
  renderBuyNow,
  hideLegal,
}: ExpressCheckoutProps) {
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode: string }

  const [walletSupported, setWalletSupported] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  // A wallet token can only be used once: a new session after each attempt.
  const [moneiSessionId, setMoneiSessionId] = useState(newMoneiSessionId)

  const containerRef = useRef<HTMLDivElement>(null)
  const componentRef = useRef<MoneiComponent | null>(null)
  const cartIdRef = useRef<string | null>(null)
  const optionIdRef = useRef<string | undefined>(undefined)
  const sourceRef = useRef(source)
  sourceRef.current = source

  const key = sourceKey(source)

  // A different product, variant or quantity needs a new buy-now cart.
  useEffect(() => {
    cartIdRef.current = source?.type === "cart" ? source.cartId : null
    optionIdRef.current = undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const ensureCart = useCallback(async () => {
    const s = sourceRef.current
    if (!s) {
      throw new Error("Selecciona una opción del producto.")
    }
    if (s.type === "cart") {
      return s.cartId
    }
    if (!cartIdRef.current) {
      const { cartId } = await createBuyNowCart({
        countryCode,
        variantId: s.variantId,
        quantity: s.quantity,
      })
      cartIdRef.current = cartId
    }
    return cartIdRef.current
  }, [countryCode])

  const handleResult = useCallback(
    (result: Awaited<ReturnType<typeof payWithWallet>>) => {
      if (!result.ok) {
        setError(result.error)
        setBusy(false)
        return
      }
      if ("redirectUrl" in result) {
        window.location.href = result.redirectUrl
        return
      }
      router.push(result.orderUrl)
    },
    [router]
  )

  // Apple Pay / Google Pay (MONEI PaymentRequest, accountId flow).
  useEffect(() => {
    // Apple Pay and Google Pay only work over HTTPS (or localhost).
    if (!MONEI_ACCOUNT_ID || !key || !window.isSecureContext) {
      return
    }
    let cancelled = false

    loadMoneiJs()
      .then((monei) => {
        if (cancelled || !containerRef.current) {
          return
        }
        const component = monei.PaymentRequest({
          accountId: MONEI_ACCOUNT_ID,
          sessionId: moneiSessionId,
          amount,
          currency: currency.toUpperCase(),
          language: "es",
          requestShipping: true,
          requestBilling: true,
          style: { height: 42, type: "buy", color: "black", borderRadius: 21 },
          onLoad: (supported) => !cancelled && setWalletSupported(supported),
          onBeforeOpen: () => {
            setError(null)
            return !!sourceRef.current
          },
          async onShippingAddressChange(address) {
            try {
              const cartId = await ensureCart()
              const quote = await quoteExpressShipping({
                cartId,
                countryCode,
                address,
              })
              if (quote.error) {
                setError(quote.error)
                return { shippingOptions: [] }
              }
              optionIdRef.current = quote.shippingOptions[0]?.id
              return {
                shippingOptions: quote.shippingOptions.map((o, i) => ({
                  ...o,
                  selected: i === 0,
                })),
                amount: quote.amount,
              }
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e))
              return { shippingOptions: [] }
            }
          },
          async onShippingOptionChange(option) {
            optionIdRef.current = option.id
            const cartId = await ensureCart()
            return selectExpressShipping({ cartId, optionId: option.id })
          },
          async onSubmit(result) {
            if (result.error || !result.token) {
              setError(result.error ?? "No se ha podido completar el pago.")
              return
            }
            setBusy(true)
            try {
              const cartId = await ensureCart()
              const res = await payWithWallet({
                cartId,
                countryCode,
                token: result.token,
                moneiSessionId,
                finalAmount: result.finalAmount,
                shippingOptionId: result.shippingOption?.id ?? optionIdRef.current,
                shippingDetails: result.shippingDetails,
                billingDetails: result.billingDetails,
              })
              if (!res.ok) {
                setMoneiSessionId(newMoneiSessionId())
              }
              handleResult(res)
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e))
              setMoneiSessionId(newMoneiSessionId())
              setBusy(false)
            }
          },
          onError: (e) => {
            console.error("MONEI PaymentRequest", e)
            // MONEI may pass a plain object instead of an Error.
            const err = e as { message?: string; statusMessage?: string }
            setError(err?.message || err?.statusMessage || "No se ha podido cargar el pago con Apple Pay / Google Pay.")
          },
        })
        component.render(containerRef.current)
        componentRef.current = component
      })
      .catch(() => setWalletSupported(false))

    return () => {
      cancelled = true
      componentRef.current?.destroy()
      componentRef.current = null
    }
    // Recreated for a new session or source; amount changes use updateProps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, moneiSessionId, countryCode, currency])

  useEffect(() => {
    componentRef.current?.updateProps({ amount })
  }, [amount])

  const isDisabled = !!disabled || !source || busy || redirecting

  const openCheckout = async () => {
    setError(null)
    setRedirecting(true)
    try {
      if (sourceRef.current?.type === "cart") {
        router.push(`/${countryCode}/checkout`)
        return
      }
      // "Comprar ya" keeps its own cart, so the basket is left untouched.
      const cartId = await ensureCart()
      router.push(`/${countryCode}/checkout?cart=${encodeURIComponent(cartId)}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setRedirecting(false)
    }
  }

  return (
    <div className="flex flex-col gap-y-2" data-testid="express-checkout">
      {MONEI_ACCOUNT_ID && (
        <div
          className={
            walletSupported
              ? isDisabled
                ? "pointer-events-none opacity-50"
                : undefined
              : "hidden"
          }
        >
          <div ref={containerRef} data-testid="wallet-button" />
        </div>
      )}
      {renderBuyNow?.(openCheckout, isDisabled, redirecting)}
      {busy && (
        <p className="text-xs text-[#71717a]">Procesando el pago...</p>
      )}
      <ErrorMessage error={error} data-testid="express-checkout-error" />
      <p className={hideLegal ? "hidden" : "text-[11px] leading-tight text-[#71717a]"}>
        Al pagar aceptas las{" "}
        <LocalizedClientLink href="/legal/condiciones" className="zl-link">
          condiciones de venta
        </LocalizedClientLink>{" "}
        y la{" "}
        <LocalizedClientLink href="/legal/privacidad" className="zl-link">
          privacidad
        </LocalizedClientLink>
        .
      </p>
    </div>
  )
}
